import { useState, useRef } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { DocumentIcon } from "@heroicons/react/24/outline";
import { BlobServiceClient, BlockBlobClient } from "@azure/storage-blob";
import { getBlobSasToken } from "../api/api-methods";
import { useCreateDocument } from "../custom-hooks/queries";
import { v4 as uuidv4 } from "uuid";

export default function UploadReportModal({
  open,
  setOpen,
  providerId,
  vqaId,
  vqaDetails,
  refetch,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const newGuid = uuidv4();

  const { mutate: createDocumentMutation } = useCreateDocument();

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    setIsUploading(true);

    try {
      const blobData = await getBlobSasToken("documents", newGuid);

      const { storage_account_name, container_name, blob_path, sas_token } =
        blobData;
      // const sas_token =
      //   "sp=racwdli&st=2024-11-21T11:00:31Z&se=2024-11-21T19:00:31Z&sv=2022-11-02&sr=c&sig=RMm%2FriV3I2OBaCbsqBkXC3zPgugt8gHcZcsow0PidGc%3D";

      const blobUrl = `https://${storage_account_name}.blob.core.windows.net/${container_name}/${selectedFile.name}?${sas_token}`;

      const blobClient = new BlockBlobClient(blobUrl);

      await blobClient.uploadData(selectedFile);

      const documentMetadataPayload = {
        document_name: selectedFile.name,
        document_type: "Sponsor Draft Report",
        document_version: "1.0",
        document_url: blobUrl.split("?")[0],
        provider_id: providerId,
        vqa_id: vqaId,
        is_draft: false,
        rfi_id: null,
        capabilities: vqaDetails.capabilities,
        document_location: "example_location",
        provider_name: "example_provider",
      };

      createDocumentMutation(documentMetadataPayload);
      alert(`File "${selectedFile.name}" uploaded successfully!`);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("An error occurred during upload. Please try again.");
    } finally {
      setIsUploading(false);
      setOpen(false);
      refetch();
    }
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      className="relative z-10"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 sm:w-full sm:max-w-xl sm:p-6"
          >
            <div className="flex items-center justify-center">
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className={`text-base font-semibold px-36 py-20 text-gray-900 cursor-pointer border-4 border-dashed rounded-md flex items-center justify-center flex-col ${
                    isUploading ? "pointer-events-none opacity-50" : ""
                  }`}
                  onClick={openFileDialog}
                >
                  <DocumentIcon className="w-20 h-20 text-gray-300" />
                  <h1 className="text-center">
                    {isUploading ? "Uploading..." : "Upload New Report"}
                  </h1>
                </DialogTitle>
                <div className="cursor-pointer p-12 flex justify-center bg-white border border-dashed border-gray-300 rounded-xl">
                  <div className="text-center">
                    <span className="inline-flex justify-center items-center size-16 bg-gray-100 text-gray-800 rounded-full">
                      <svg
                        className="shrink-0 size-6"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" x2="12" y1="3" y2="15"></line>
                      </svg>
                    </span>
                    <div className="mt-4 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
                      <span className="pe-1 font-medium text-gray-800">
                        Drop your file here or
                      </span>
                      <label
                        htmlFor="file-upload"
                        className="bg-white font-semibold text-blue-600 hover:text-blue-700 rounded-lg decoration-2 hover:underline focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 cursor-pointer"
                      >
                        browse
                      </label>
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        // onChange={handleFileSelect}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      Pick a file up to 2MB.
                    </p>
                    (
                    <div className="mt-4">
                      <p className="text-sm text-gray-700">Selected file:</p>
                    </div>
                    )
                  </div>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
